import { useCallback, useEffect, useRef, useState } from 'react';

function useModalTouchSwipe({
	modalRef,
	requestClose,
	holdDelayMs = 200,
	swipeThresholdRatio = 0.3,
}) {
	const touchStartY = useRef(null);
	const touchEndY = useRef(null);
	const holdTimerRef = useRef(null);
	const isHolding = useRef(false);
	const swipedDownRef = useRef(false);
	const [swipeOffset, setSwipeOffset] = useState(0);
	const [isSwiping, setIsSwiping] = useState(false);

	const handleTouchStart = useCallback(
		event => {
			if (event.touches && event.touches.length === 1) {
				touchStartY.current = event.touches[0].clientY;
				touchEndY.current = event.touches[0].clientY;
				isHolding.current = false;
				swipedDownRef.current = false;
				holdTimerRef.current = window.setTimeout(() => {
					isHolding.current = true;
					setIsSwiping(true);
				}, holdDelayMs);
			}
		},
		[holdDelayMs]
	);

	const handleTouchMove = useCallback(event => {
		if (event.touches && event.touches.length === 1) {
			touchEndY.current = event.touches[0].clientY;
			if (isHolding.current && touchStartY.current !== null) {
				setSwipeOffset(Math.max(0, touchEndY.current - touchStartY.current));
			}
		}
	}, []);

	const handleTouchEnd = useCallback(() => {
		window.clearTimeout(holdTimerRef.current);
		const swipeThreshold = typeof window !== 'undefined' ? window.innerHeight * swipeThresholdRatio : 0;
		const swipeDistance = touchStartY.current !== null && touchEndY.current !== null
			? touchEndY.current - touchStartY.current
			: 0;

		if (
			isHolding.current &&
			touchStartY.current !== null &&
			touchEndY.current !== null &&
			swipeDistance > swipeThreshold
		) {
			swipedDownRef.current = true;
			requestClose();
		} else {
			setSwipeOffset(0);
			swipedDownRef.current = false;
		}

		touchStartY.current = null;
		touchEndY.current = null;
		isHolding.current = false;
		setIsSwiping(false);
	}, [requestClose, swipeThresholdRatio]);

	useEffect(() => {
		const modalElement = modalRef.current;
		if (modalElement) {
			modalElement.addEventListener('touchstart', handleTouchStart);
			modalElement.addEventListener('touchmove', handleTouchMove);
			modalElement.addEventListener('touchend', handleTouchEnd);
		}

		return () => {
			window.clearTimeout(holdTimerRef.current);
			if (modalElement) {
				modalElement.removeEventListener('touchstart', handleTouchStart);
				modalElement.removeEventListener('touchmove', handleTouchMove);
				modalElement.removeEventListener('touchend', handleTouchEnd);
			}
		};
	}, [handleTouchEnd, handleTouchMove, handleTouchStart, modalRef]);

	return {
		swipeOffset,
		isSwiping,
		swipedDownRef,
		touchHandlers: {
			onTouchStart: handleTouchStart,
			onTouchMove: handleTouchMove,
			onTouchEnd: handleTouchEnd,
		},
	};
}

export default useModalTouchSwipe;